/**
 * The tour runtime.
 *
 * Deliberately FRAMEWORK-FREE: no React, no router, no app imports. It takes a
 * `Tour` and drives driver.js over whatever it can resolve, reporting progress
 * through a callback. That is what makes the React layer above it a thin
 * adapter rather than the engine itself, and what would let the same engine
 * drive a tour from a non-React surface.
 */

import { driver, type Driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';

import {
  accordionScopeFor,
  accordionTriggerFor,
  anchorTargetsSidebar,
  closeSidebarDrawer,
  collapseAccordionsExcept,
  ensureSidebarUsable,
  isVisible,
  keepAnchorInView,
  openAccordion,
  resolveAnchor,
  restoreSidebarShape,
  sidebarShape,
  waitForDom,
  type AnchorStrategy,
  type SidebarRestore,
  type SidebarShape,
} from './anchors';
import { restoreHostChrome, suppressHostChrome } from './hostChrome';
import {
  dressGuide,
  groupFits,
  guideHeight,
  guideMode,
  keepTrackerClear,
  liveGuide,
  makeRoomFor,
  releaseRoom,
  visibleBand,
} from './room';
import type { Tour, TourStep } from '../tours';

export interface TourRunState {
  /** True while the overlay is on screen. */
  running: boolean;
  /** Id of the tour being run, or null. */
  tourId: string | null;
  /** Id of the step being shown, or null. */
  stepId: string | null;
  /** Position within the steps that actually resolved (0-based), or -1. */
  index: number;
  /**
   * How many steps this run will show — resolved steps only, not authored.
   *
   * GROWS automatically as the run reaches a module that has submodules: they
   * are spliced in the moment their module is highlighted, so the walk flows
   * straight into them rather than waiting on a separate action. That is
   * honest rather than awkward: the run really did get longer, because the
   * user reached that point.
   */
  total: number;
  /**
   * Step ids in the order THIS run visits them, after skipping unresolvable
   * steps and applying `order`. The tracker computes stage states from this
   * rather than from the authored arrays, which would be out of step with what
   * the user is actually being shown.
   */
  order: string[];
}

export interface StartResult {
  started: boolean;
  /** Steps that resolved and will be shown. */
  resolved: { stepId: string; strategy: AnchorStrategy }[];
  /**
   * Steps skipped because nothing on screen matched.
   *
   * Submodule steps are absent from BOTH lists — they are opt-in, so at start
   * they are neither shown nor skipped, merely not offered yet.
   */
  skipped: string[];
}

/** driver.js marks the document while a tour is on screen. */
const DRIVER_ACTIVE_CLASS = 'driver-active';

/**
 * How long to keep following a layout that is still moving after a step change.
 *
 * 🔴 A GROUP DOES NOT OPEN OR CLOSE INSTANTLY. Radix animates the panel's height,
 * and driver.js animates its own stage over 400ms on top of that, so every
 * measurement taken in the frame a step changes is a measurement of a layout
 * mid-flight — the ring lands where the rows were on their way to, and the module
 * ends up a couple of hundred pixels from where the guide was placed for it. On a
 * tall screen that is a wobble; on a short one it is the module being off screen.
 * This is how long the run keeps re-measuring before it accepts the layout has
 * stopped. See `settleLayout`.
 */
const SETTLE_MS = 520;

/** Frames of an unchanged box that count as "the layout has stopped". */
const SETTLE_STILL_FRAMES = 2;

/**
 * How long to wait for a group's submodule rows to mount after it is opened.
 *
 * Measured at ~14ms in this shell; the budget is generous because the cost of
 * being wrong is asymmetric — a few idle frames nobody perceives, versus a
 * sub-flow that silently resolves to nothing because the check ran one frame too
 * early.
 */
const SUBMENU_MOUNT_TIMEOUT_MS = 600;

/**
 * How long a RESUME will wait for the host to mount before rebuilding the run.
 *
 * Generous, because the wait is invisible — the tour simply reappears a beat
 * later — while getting it wrong is not: a run resolved against a half-mounted
 * page comes back truncated, and a truncated run reads as the tour having jumped
 * to the end. Bounded, because a module that is genuinely gone will never arrive
 * and the resume still has to happen. See `waitForHostToSettle`.
 */
const HOST_SETTLE_TIMEOUT_MS = 2500;

/**
 * How long the resolvable count must hold STILL before the page counts as
 * settled.
 *
 * 🔴 A DURATION, NOT A FRAME COUNT, AND THE DIFFERENCE IS THE WHOLE BUG. This
 * was three frames — about 48ms — which is shorter than the gap between the
 * commits of a single route change. The app's top bar mounts before its sidebar,
 * so the count sat at 1 for three frames and the loop declared the page settled
 * with exactly one anchor resolvable: the truncated run all over again, from the
 * code written to prevent it. Caught by `tourResumeSettle.test.ts`, which is why
 * that suite exercises the rule rather than only pinning it.
 *
 * 300ms comfortably spans a route's commits without being noticeable — and it is
 * only ever paid when the paused count does NOT come back, since a page that has
 * everything returns through the fast path above instead.
 */
const HOST_SETTLE_STILL_MS = 300;

/**
 * Attributes driver.js COMMANDEERS on whatever element it is highlighting.
 *
 * 🔴 It stamps `aria-expanded="true"`, `aria-controls="driver-popover-content"`
 * and `aria-haspopup="dialog"` onto the element, then REMOVES all three when the
 * tour moves on — it does not put back what was there. On this shell that means
 * every sidebar accordion row the tour visits comes out of the run having lost its
 * real `aria-expanded` and `aria-controls`, so it stops announcing itself as
 * expandable to a screen reader until React happens to re-render it. A guided tour
 * degrading the accessibility of the thing it just introduced is not acceptable,
 * so the engine snapshots these three before driver writes them and puts them back
 * on teardown.
 */
const DRIVER_ARIA_ATTRS = ['aria-expanded', 'aria-controls', 'aria-haspopup'] as const;

/**
 * 🔴 THIS CLASS MUST NOT CONTAIN "driver-popover".
 *
 * driver.js installs CAPTURE-phase document listeners and, for any click inside
 * the popover whose target's className includes `driver-popover`, calls
 * `preventDefault` + `stopImmediatePropagation` before the event reaches the
 * element. A button named `driver-popover-task-btn` would therefore render
 * perfectly and be completely dead.
 */
const TASK_CLASS = 'tour-guide-task';

/** The popover's "skip the rest of this module" control. Same naming rule as above. */
const SKIP_CLASS = 'tour-guide-skip';

/**
 * Everything needed to put an interrupted run back exactly where it was.
 *
 * A guided creation flow takes the screen over completely, so the tour is torn
 * down rather than layered under it — see `TaskEngine`. That means the position
 * has to be captured as DATA, because driver.js keeps none of its state across a
 * destroy.
 *
 * 🔴 `expanded` IS THE PART THAT IS EASY TO MISS. A submodule step does not exist
 * in a fresh run: it is spliced in when its group is opened. A token that recorded
 * only the step id would therefore fail to find it on the way back — and every
 * flow so far hangs off a submodule step, so that is the normal case, not an edge.
 */
export interface TourResumeToken {
  tourId: string;
  stepId: string;
  /** Module step ids whose sub-flows were open, in the order they were opened. */
  expanded: string[];
  /**
   * How many top-level stops the run held when it was paused.
   *
   * 🔴 CARRIED PURELY SO THE RESUME CAN TELL A REBUILT RUN FROM A COLLAPSED ONE.
   * `start()` resolves every anchor ONCE, synchronously, and keeps only what was
   * on screen at that instant — so resuming against a page that has not finished
   * mounting yields a run with a handful of stops, or one. There is no signal in
   * the run itself that says "this is wrong"; the only thing that knows is what
   * the run looked like a moment ago. See `resumeFrom`.
   */
  moduleCount: number;
}

export interface TourEngineOptions {
  onState(state: TourRunState): void;
  /** Called once when a run reaches its final step. */
  onComplete(tourId: string): void;
  /** Accent colour for the primary button. */
  accent?: string;
  /**
   * Does this step have a guided creation flow to offer? Return its button copy,
   * or null.
   *
   * A CALLBACK rather than a list the engine holds, so the engine stays ignorant
   * of what a task is — it renders a button and reports the click. Which steps
   * have flows lives with the flows.
   */
  taskOffer?(stepId: string): { label: string } | null;
  /** The offer was taken. The host pauses the tour and starts the flow. */
  onTaskStart?(stepId: string): void;
}

/** A step that resolved, paired with the element it will highlight. */
interface RunEntry {
  step: TourStep;
  el: HTMLElement;
  strategy: AnchorStrategy;
  /**
   * Module labels whose whole accordion item this step is highlighting, once its
   * sub-flow has been opened — see the re-target in `expandChildren`.
   *
   * 🔴 REMEMBERED, BECAUSE RE-RESOLVING WOULD SILENTLY UNDO IT. `syncActiveAnchor`
   * re-resolves a step's anchor whenever the layout moves under it, and this
   * step's anchor resolves to the TRIGGER ROW — so a plain re-resolve would shrink
   * the highlight from "the module and its submodules" back to a single row,
   * mid-step, the first time anything scrolled.
   */
  scopeLabels?: string[];
}

export class TourEngine {
  private instance: Driver | null = null;
  private options: TourEngineOptions;

  /**
   * The furthest step reached this run.
   *
   * 🔴 Completion CANNOT be read from `getActiveIndex()` inside `onDestroyed` —
   * driver.js has already torn its own state down by then and reports 0, so
   * `0 >= total - 1` is false for every multi-step tour and a finished run is
   * never recorded. The high-water mark, updated as steps highlight, is the only
   * reading that survives teardown.
   */
  private furthestIndex = -1;

  /** Host chrome hidden by this run, restored on teardown. */
  private suppressed: HTMLElement[] = [];

  /** Step ids in this run's visit order. Empty when idle. */
  private runOrder: string[] = [];

  /**
   * Id of the tour being run.
   *
   * Held as a field as well as in the run closure because `pauseForTask` has to
   * name the tour in a token it hands back to the caller, and it has no `tour` in
   * scope.
   */
  private tourId: string | null = null;

  /** The live run: resolved steps with their elements. Index-aligned with `driveSteps`. */
  private run: RunEntry[] = [];

  /**
   * The array driver.js is configured with.
   *
   * 🔴 MUTATED IN PLACE, never replaced. driver.js keeps the reference it was
   * handed and re-reads it on every transition, so splicing into this array is
   * all it takes to lengthen a live run. The obvious alternative, `setSteps()`,
   * WIPES driver's entire state object — including `isInitialized` and its handle
   * on the rendered popover — so the next `drive()` re-registers its keyboard and
   * overlay listeners and builds a second popover, leaving the first orphaned in
   * the DOM and every arrow-key press acting twice.
   */
  private driveSteps: DriveStep[] = [];

  /** Submodule steps grouped by the module step they hang off. */
  private childrenByParent = new Map<string, TourStep[]>();

  /**
   * Parents whose group is actually on screen, with their child count. Sampled
   * from the DOM rather than from the authored step list, because which modules
   * have a group depends on the user's plan and permissions.
   *
   * 🔴 RE-SAMPLED WHEN THE NAVIGATION CHANGES SHAPE, not once per run. This used
   * to be resolved once, on the reasoning that which rows are collapsible cannot
   * change mid-run — which is false at a breakpoint: an icon-collapsed rail
   * renders a module with submodules as a HOVER CARD and has no accordions at
   * all (see `accordionTriggerFor`), so a run that started there recorded no
   * expandable modules and kept offering none after the rail was expanded.
   */
  private expandableCounts = new Map<string, number>();

  /** Parents whose sub-flow has already been spliced in, so it is offered once. */
  private expanded = new Set<string>();

  /**
   * Serializes expansions so overlapping requests QUEUE instead of one
   * silently no-oping while another is mid-flight.
   *
   * 🔴 A single in-flight flag caused a real bug here: `resumeFrom` replays
   * every previously-open module in sequence, but `start()`'s own auto-expand
   * (see `onHighlightStarted`) can already be mid-flight for the run's first
   * step by the time that replay begins. A boolean guard made every replay
   * call, for as long as that first expansion was pending, return immediately
   * as a no-op — silently dropping the very expansions the resume depends on.
   * Chaining onto this promise makes a later caller WAIT for the earlier one
   * instead of being dropped by it.
   */
  private expandChain: Promise<void> = Promise.resolve();

  /**
   * Host attributes driver.js is about to overwrite, keyed by element, captured
   * the first time each one is highlighted. See `DRIVER_ARIA_ATTRS`.
   */
  private hostAria = new Map<HTMLElement, (string | null)[]>();

  /**
   * Navigation shape changes this run asked the host for, undone on teardown.
   * See `ensureSidebarUsable`.
   */
  private sidebarRestore: SidebarRestore | null = null;

  /**
   * The navigation shape this run's elements were resolved against.
   *
   * Compared on resize so a breakpoint crossing is handled ONCE, rather than
   * re-resolving the whole run on every pixel of a window drag. See
   * `adaptToViewport`.
   */
  private shape: SidebarShape = 'absent';

  /**
   * Frame handle of the settle loop, so a new step cancels the previous one's.
   * See `settleLayout`.
   */
  private settleFrame: number | null = null;

  /** Detaches the scroll/resize listeners that keep the highlight aligned. */
  private stopTracking: (() => void) | null = null;

  /**
   * Detaches the Escape handler that replaces driver's own.
   *
   * Held separately from `stopTracking` because it is bound at a different
   * moment — the run has to be able to end from the keyboard for the whole of
   * its life, including the stretch before the first stop has finished settling.
   */
  private stopEscape: (() => void) | null = null;

  constructor(options: TourEngineOptions) {
    this.options = options;
  }

  /**
   * True when any tour (this engine's or another) currently owns the screen.
   *
   * driver.js 1.4 marks `document.body`; earlier versions marked the document
   * element. Both are checked so the re-entrancy guard cannot silently stop
   * working on a version bump — reading only one of them is how it came to be
   * checking the wrong node in the first place.
   */
  static isAnyTourActive(): boolean {
    return (
      document.body.classList.contains(DRIVER_ACTIVE_CLASS) ||
      document.documentElement.classList.contains(DRIVER_ACTIVE_CLASS)
    );
  }

  /**
   * The way a run should be STARTED: prepare the navigation, then start.
   *
   * `start` stays synchronous (and public) because it is the whole of the
   * resolve-and-drive logic and is easier to reason about without an await in
   * it — but every real caller wants the async wrapper, because the shape of
   * the sidebar decides which steps can resolve AT ALL. Preparing it after the
   * run had already sampled the DOM would be too late: the module steps would
   * be in `skipped` for the rest of the run.
   */
  async open(tour: Tour): Promise<StartResult> {
    if (TourEngine.isAnyTourActive()) {
      return { started: false, resolved: [], skipped: [] };
    }
    // `??=` so a re-prep that changes nothing cannot overwrite what an earlier
    // one recorded — teardown has to undo the FIRST change, not the last.
    this.sidebarRestore ??= await ensureSidebarUsable();
    const result = this.start(tour);
    // Nothing to come back from: put the navigation back rather than leaving a
    // drawer open over a tour that never started.
    if (!result.started) {
      restoreSidebarShape(this.sidebarRestore);
      this.sidebarRestore = null;
    }
    return result;
  }

  start(tour: Tour): StartResult {
    // Re-entrancy guard. Starting a second tour over a live one stacks two
    // overlays and makes the first unreachable.
    if (TourEngine.isAnyTourActive()) {
      return { started: false, resolved: [], skipped: [] };
    }

    // Submodule steps are held back: they join the run only when their module's
    // stop offers them and the user takes it.
    const childrenByParent = new Map<string, TourStep[]>();
    const topLevel: TourStep[] = [];
    for (const step of tour.steps) {
      if (step.parentStepId === undefined) {
        topLevel.push(step);
        continue;
      }
      const siblings = childrenByParent.get(step.parentStepId);
      if (siblings) siblings.push(step);
      else childrenByParent.set(step.parentStepId, [step]);
    }

    const resolved: RunEntry[] = [];
    const skipped: string[] = [];

    for (const step of topLevel) {
      const hit = resolveAnchor(step.anchor);
      if (hit) resolved.push({ step, el: hit.element, strategy: hit.strategy });
      else skipped.push(step.id);
    }

    // 🔴 Sequence a menu walkthrough by ON-SCREEN position, not by the order the
    // steps happen to be written in. The two drifted once — the authored list had
    // Ads before Reports while the sidebar renders Reports first, so the run
    // jumped past Reports and then back up to it, which reads as a bug to
    // someone watching the highlight move. Sorting here means the tour follows
    // whatever the UI actually shows, including after a reorder, and no author
    // has to keep a hand-written list in sync with a component.
    //
    // Groups come FIRST, and geometry only breaks ties inside a group: the
    // header sits above the sidebar, so a purely geometric sort would put a
    // header-anchored step in front of the first menu entry and the tour would
    // stop starting where it says it does.
    //
    // Only top-level steps are sorted. A submodule row cannot be measured before
    // its group is open, so children are ordered as authored — see
    // `TourStep.parentStepId`.
    if (tour.order === 'dom') {
      resolved.sort((a, b) => {
        const groupDelta = (a.step.group ?? 0) - (b.step.group ?? 0);
        if (groupDelta !== 0) return groupDelta;
        if (a.el === b.el) return 0;
        const rel = a.el.compareDocumentPosition(b.el);
        if (rel & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
        if (rel & Node.DOCUMENT_POSITION_PRECEDING) return 1;
        // Disconnected / unrelated nodes have no defined order; leaving them as
        // authored is better than an arbitrary swap.
        return 0;
      });
    }

    if (resolved.length === 0) {
      // Silent success would read as a dead button, so say so where a developer
      // will see it. Not a thrown error: an empty tour is a degraded screen, not
      // a broken app.
      console.warn(`[tour-guide] "${tour.label}": no targets on screen — nothing to show.`);
      return { started: false, resolved: [], skipped };
    }

    this.furthestIndex = -1;
    this.suppressed = suppressHostChrome();
    this.tourId = tour.id;
    this.run = resolved;
    // Set BEFORE building drive steps — `driveStep()` reads it to append a
    // module's submodule summary to its own description.
    this.childrenByParent = childrenByParent;
    this.driveSteps = resolved.map((entry) => this.driveStep(entry));
    this.runOrder = resolved.map((r) => r.step.id);
    this.expanded = new Set();
    this.expandChain = Promise.resolve();

    this.recountExpandable();
    // The shape the run was resolved against, so a change to it is detectable.
    // See `adaptToViewport`.
    this.shape = sidebarShape();

    const instance = driver({
      steps: this.driveSteps,
      /**
       * 🔴 OFF, AND THIS IS THE FIX FOR "IT HIGHLIGHTS THE WRONG MODULE".
       *
       * driver.js changes the ring and the words at DIFFERENT TIMES. Read its
       * transition callback: the stage starts sliding toward the new element on
       * the first frame (`animate && elapsed < 400 ? fe(...)`), while the popover
       * is only re-rendered once `400 - elapsed <= 200` — i.e. ~200ms LATER, and
       * only on the animated path (`!u && o.popover && Q(e, o)` is the immediate
       * one).
       *
       * So on every single step change there was a ~200ms window where the ring
       * had already moved to the NEXT module while the card still carried the
       * previous module's title, body and "N of M". Measured in a real browser at
       * 1280×620: 14–44 frames per transition of "guide says Inbox, ring is on New
       * Campaigns". That is exactly what the screenshots caught — nothing was
       * mis-resolved, mis-scrolled or one row off; the two halves of the same step
       * were simply drawn out of step with each other.
       *
       * With animation off, `u` is false, so driver renders the popover
       * SYNCHRONOUSLY and its first frame snaps the stage with `te(e)` — the ring
       * and the words change together, always describing the same module. It also
       * stops the ring travelling ACROSS the modules in between, which on a short
       * sidebar is the other half of "it highlighted the wrong one".
       *
       * The cost is the slide and the overlay fade. That is a fair trade: a
       * highlight that is briefly wrong is worse than one that is never animated,
       * and on a short screen the slide was the more distracting of the two.
       */
      animate: false,
      showProgress: true,
      /**
       * 🔴 FALSE, SO A CLICK ON THE DIM DOES NOTHING AT ALL.
       *
       * `driver.css` already makes the page inert while a tour runs
       * (`.driver-active * { pointer-events: none }`, with this module
       * re-enabling the popover, the tracker and the highlighted element) — so
       * the dim was never passing clicks THROUGH to the app. What it did instead
       * was worse and is the reported "clicking outside does something other
       * than the tour": with `allowClose`, driver treats any click outside the
       * active element as a request to END THE RUN. One stray click and the
       * whole tour is gone, along with everything a finished run restores.
       *
       * The way out is unchanged and still obvious: the tracker's × ("End
       * tour"), and Done on the last stop. `TourTracker`'s own
       * `stopPropagation` becomes belt-and-braces rather than load-bearing —
       * left in place because it costs nothing and this flag is one edit away
       * from being flipped back.
       *
       * 🔴 THIS ALSO TAKES ESCAPE AWAY FROM DRIVER, which gates both on the same
       * flag — so `bindEscapeToStop` below puts Escape back explicitly. Removing
       * a keyboard escape while making the background inert would leave a
       * keyboard user with no way out but hunting for the ×.
       */
      allowClose: false,
      overlayColor: 'rgba(15, 23, 42, 0.68)',
      stagePadding: 6,
      stageRadius: 8,
      popoverClass: 'tour-guide-popover',
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      doneBtnText: 'Done',
      // 🔴 ALL navigation is routed through `navigateTo`, including driver's own
      // buttons and its arrow-key handlers (which call these same hooks). A step
      // inside a collapsed group has no element on screen, so something has to
      // reopen that group BEFORE the highlight — and if the popover's buttons
      // bypassed that, the same move would work from the tracker and land on
      // nothing from the popover.
      onNextClick: () => {
        void this.afterPendingExpand(instance, () => {
          void this.navigateTo((instance.getActiveIndex() ?? 0) + 1);
        });
      },
      onPrevClick: () => {
        void this.afterPendingExpand(instance, () => {
          const at = instance.getActiveIndex() ?? 0;
          // Driver disables the Back button on the first step but the left arrow
          // key still fires this; without the guard it would step to -1, find no
          // entry and end the tour.
          if (at > 0) void this.navigateTo(at - 1);
        });
      },
      // Rebuilt from scratch by driver.js on every step, so the button below is
      // re-decided each time rather than lingering where it does not belong.
      onPopoverRender: (popover) => {
        const at = instance.getActiveIndex() ?? -1;
        const entry = this.run[at];
        if (!entry) return;
        // Between the description and the Back/Next row: both are alternatives
        // to moving on, so they read in the place where that decision is made.
        //
        // The TASK comes first when a step offers both. "Build one" is the
        // stronger invitation — doing beats reading a list of names — and a step
        // with a flow is one this shell wants the visitor to actually try.
        const task = this.buildTaskButton(entry.step);
        if (task) popover.wrapper.insertBefore(task, popover.footer);
        // After the task offer and before Back/Next: all three are answers to
        // "what now", and this is the quietest of them.
        const skip = this.buildSkipModuleButton(at);
        if (skip) popover.wrapper.insertBefore(skip, popover.footer);
        this.keepModalsOpen(popover.wrapper);
        // 🔴 LAST, AND IN THIS HOOK RATHER THAN `onHighlighted`. The card is only
        // now complete — the two buttons above are this module's, so its height
        // is not knowable before they are in it — and driver.js positions the
        // card on the very next line of its own render, reading both the step's
        // `side` and the element's rect fresh. Deciding the placement and making
        // room here therefore lands BEFORE the placement that consumes it: no
        // visible jump, and no second pass. `onHighlighted` fires only after the
        // stage animation, which is 400ms of the guide sitting somewhere the
        // module is not.
        this.fitStep(at, entry, popover.wrapper);
      },
      // Runs BEFORE driver.js writes its own attributes onto the element, which
      // is the only moment the host's originals are still readable.
      onHighlightStarted: (element) => {
        this.dropStaleActiveMarks(element);
        this.rememberHostAria(element);
        const at = instance.getActiveIndex?.() ?? 0;
        this.furthestIndex = Math.max(this.furthestIndex, at);
        const stepId = this.run[at]?.step.id ?? null;

        // Flow straight into a module's submodules the moment it is reached,
        // rather than waiting on a separate "explore" action — see
        // `expandChildren`. Guarded by `expanded` so this fires once per module;
        // the splice below re-highlights the same step, which re-enters here and
        // finds `expanded` already set.
        if (stepId && this.expandableCounts.has(stepId) && !this.expanded.has(stepId)) {
          void this.expandChildren(stepId);
        }

        this.options.onState({
          running: true,
          tourId: tour.id,
          stepId,
          index: at,
          total: this.run.length,
          order: this.runOrder,
        });
      },
      onDestroyed: () => {
        const reachedEnd = this.furthestIndex >= this.run.length - 1;
        // Restore the host FIRST — before any callback that could throw, so a
        // consumer's error can never leave the app's own UI hidden or its markup
        // altered. driver.js has already stripped its own attributes by the time
        // this runs, so putting the originals back here wins.
        this.stopTracking?.();
        this.stopTracking = null;
        // A key handler left on `document` would go on swallowing Escape for the
        // rest of the page's life, long after the tour it belonged to had ended.
        this.stopEscape?.();
        this.stopEscape = null;
        if (this.settleFrame !== null) window.cancelAnimationFrame(this.settleFrame);
        this.settleFrame = null;
        restoreHostChrome(this.suppressed);
        this.suppressed = [];
        this.restoreHostAria();
        // The gap held open around the module being described, and any space held
        // open past the end of the sidebar's list, go back with it — see `room.ts`.
        releaseRoom();
        // The navigation is put back to the shape the user had it in — unlike an
        // accordion group, which is deliberately left open. See `SidebarRestore`.
        restoreSidebarShape(this.sidebarRestore);
        this.sidebarRestore = null;
        this.instance = null;
        const total = this.run.length;
        this.tourId = null;
        this.runOrder = [];
        this.run = [];
        this.driveSteps = [];
        this.childrenByParent = new Map();
        this.expandableCounts = new Map();
        this.expanded = new Set();
        this.expandChain = Promise.resolve();
        this.shape = 'absent';
        // A group opened for a sub-flow is deliberately LEFT OPEN. It is exactly
        // the state a user reaches by clicking that row themselves, the host
        // closes whichever group was open when the next one opens, and closing it
        // here would snap the sidebar shut under the cursor the moment the tour
        // ends.
        this.options.onState({
          running: false,
          tourId: null,
          stepId: null,
          index: -1,
          total,
          order: [],
        });
        if (reachedEnd) this.options.onComplete(tour.id);
      },
    });

    this.instance = instance;
    this.bindEscapeToStop();
    instance.drive();
    this.startTracking(instance);

    // 🔴 THE FIRST STOP GETS THE SAME PREPARATION AS EVERY LATER ONE. `drive()`
    // highlights it directly, so it is the one move that does not go through
    // `navigateTo` — without this, a run starting while the user had some other
    // module expanded began with that module's rows filling the list, which on a
    // short screen is the reported failure on step one.
    const first = this.run[0];
    if (first) {
      void this.prepareGroups(first).then(() => {
        if (this.instance === instance) this.settleLayout(instance);
      });
    }

    return {
      started: true,
      resolved: resolved.map((r) => ({ stepId: r.step.id, strategy: r.strategy })),
      skipped,
    };
  }

  /**
   * Jump the live run to a step, by id. Returns false when it is not part of
   * this run — a stage whose steps were all skipped is not reachable, and
   * silently doing nothing is better than moving to an unrelated step. A
   * submodule step is reachable only once its sub-flow has been expanded.
   *
   * Returns synchronously (whether the step is in the run) while the move itself
   * finishes asynchronously, because reaching a step in a collapsed group means
   * waiting for that group to open. Callers only need to know it was accepted.
   */
  goToStep(stepId: string): boolean {
    if (!this.instance) return false;
    const index = this.runOrder.indexOf(stepId);
    if (index < 0) return false;
    void this.navigateTo(index);
    return true;
  }

  /**
   * `goToStep`, awaited.
   *
   * 🔴 EXISTS BECAUSE "JUMP, THEN MOVE ON" WAS RACING ITSELF. Reaching a step
   * inside a collapsed group means waiting for that group to open, so the jump
   * finishes a frame or more after the call returns — and driver.js still
   * reports the OLD active index in the meantime. Anything computed from that
   * index (a Next, a skip to the next module) therefore moved relative to
   * wherever the run happened to be standing, not relative to the step it had
   * just been sent to. That is what made "back to the tour" after a guided flow
   * land on an unrelated stop.
   */
  async goTo(stepId: string): Promise<boolean> {
    if (!this.instance) return false;
    const index = this.runOrder.indexOf(stepId);
    if (index < 0) return false;
    await this.navigateTo(index);
    return true;
  }

  /** Advance one step, ending the tour when there is nothing after this one. */
  next(): boolean {
    const instance = this.instance;
    if (!instance) return false;
    void this.afterPendingExpand(instance, () => {
      void this.navigateTo((instance.getActiveIndex() ?? 0) + 1);
    });
    return true;
  }

  /**
   * Go back one stop. No-op on the first.
   *
   * 🔴 ROUTED THROUGH `afterPendingExpand`, exactly like `next()`, and that is
   * not symmetry for its own sake. A module queues its sub-flow the instant it
   * is highlighted, and that expansion ends by RE-HIGHLIGHTING the module (see
   * `reanchor`) so the popover picks up the longer run. A Back pressed inside
   * that window moved the run backwards and was then dragged straight forward
   * again by the reanchor — which reads, precisely, as "the Back button does
   * nothing".
   */
  previous(): boolean {
    const instance = this.instance;
    if (!instance) return false;
    void this.afterPendingExpand(instance, () => {
      const at = instance.getActiveIndex() ?? 0;
      if (at > 0) void this.navigateTo(at - 1);
    });
    return true;
  }

  /**
   * Leave the module the run is standing in and go to the NEXT module.
   *
   * "Module" is a top-level stop; a submodule stop belongs to whichever module
   * precedes it. So from anywhere inside a module — its own stop or any of the
   * sub-flow stops that were spliced in after it — this lands on the next
   * top-level stop, skipping the rest of the current module's contents. That is
   * what skipping a module means, and it is why this cannot be expressed as some
   * number of `next()` calls: how many stops are left inside a module depends on
   * what its sub-flow resolved to.
   *
   * Running out of modules ends the run, which is what Next on the last stop
   * already does.
   *
   * `fromStepId` names the stop to measure from, for a caller that has just sent
   * the run somewhere and cannot trust driver's active index yet.
   */
  nextModule(fromStepId?: string): boolean {
    const instance = this.instance;
    if (!instance) return false;
    void this.afterPendingExpand(instance, () => {
      const at = this.indexOfStop(instance, fromStepId);
      void this.navigateTo(this.nextModuleIndex(this.moduleIndexFor(at)));
    });
    return true;
  }

  /**
   * Go back one stop from a NAMED stop rather than from wherever driver thinks
   * the run is. Used to step the tour back after leaving a guided flow.
   */
  previousFrom(fromStepId?: string): boolean {
    const instance = this.instance;
    if (!instance) return false;
    void this.afterPendingExpand(instance, () => {
      const at = this.indexOfStop(instance, fromStepId);
      if (at > 0) void this.navigateTo(at - 1);
    });
    return true;
  }

  /** Position of a named stop, falling back to driver's own active index. */
  private indexOfStop(instance: Driver, stepId?: string): number {
    const named = stepId ? this.runOrder.indexOf(stepId) : -1;
    return named >= 0 ? named : instance.getActiveIndex() ?? 0;
  }

  /**
   * The module stop that OWNS the given position — itself, if it is one.
   *
   * A submodule stop is always preceded by its module: children are spliced in
   * directly after their parent, so walking back to the first top-level stop is
   * the same thing as naming the module the user is inside.
   */
  private moduleIndexFor(at: number): number {
    for (let i = Math.min(at, this.run.length - 1); i >= 0; i--) {
      if (this.run[i].step.parentStepId === undefined) return i;
    }
    return at;
  }

  /**
   * The next module stop after `from`, or one past the end when there is none —
   * which `navigateTo` reads as "finish the run".
   */
  private nextModuleIndex(from: number): number {
    for (let i = from + 1; i < this.run.length; i++) {
      if (this.run[i].step.parentStepId === undefined) return i;
    }
    return this.run.length;
  }

  /**
   * Escape ends the run, because driver no longer does it for us.
   *
   * 🔴 THE OTHER HALF OF `allowClose: false`. That flag gates driver's
   * overlay-click close AND its Escape handler with one boolean, so turning off
   * the accidental dismissal took the deliberate one with it. This puts the
   * deliberate one back and nothing else.
   *
   * CAPTURE PHASE, so a run standing on a step inside an open host dialog still
   * ends on Escape rather than the dialog eating the key first — the tour is the
   * outer thing here, and leaving the user in a dialog the walkthrough opened
   * with the walkthrough still running is the state this avoids.
   *
   * `stop()` rather than `destroy()` directly: it is the same path the tracker's
   * × takes, so a keyboard exit and a pointer exit restore the host identically.
   */
  private bindEscapeToStop(): void {
    this.stopEscape?.();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (!this.instance) return;
      this.stop();
    };

    document.addEventListener('keydown', onKeyDown, true);
    this.stopEscape = () => document.removeEventListener('keydown', onKeyDown, true);
  }

  stop(): void {
    this.instance?.destroy();
    this.instance = null;
  }

  /* ---------------------------------------------------------------- *
   * Handing the screen to a guided creation flow, and taking it back.
   * ---------------------------------------------------------------- */

  /**
   * Tear the tour down, returning what is needed to restore it.
   *
   * 🔴 CAPTURED BEFORE `stop()`, because `onDestroyed` clears `expanded`, `run`
   * and `tourId` — reading them afterwards yields an empty token that resumes to
   * step one of a run with no sub-flows open.
   *
   * Returns null when nothing was running, which the caller reads as "there is no
   * tour to come back to" rather than as a failure.
   */
  pauseForTask(preferredStepId?: string): TourResumeToken | null {
    const instance = this.instance;
    if (!instance || !this.tourId) return null;

    const at = instance.getActiveIndex() ?? -1;
    // `preferredStepId` is the step the FLOW belongs to, which is not necessarily
    // the one being shown — the tracker can launch a flow from a row the tour has
    // not reached. Coming back to the flow's own step is the useful landing, and
    // it has to be in the run to be reachable at all, hence the membership test.
    const requested =
      preferredStepId && this.runOrder.includes(preferredStepId) ? preferredStepId : null;
    const stepId = requested ?? this.run[at]?.step.id ?? null;
    const token: TourResumeToken | null = stepId
      ? {
          tourId: this.tourId,
          stepId,
          expanded: [...this.expanded],
          // Top-level stops only — submodules are spliced in on demand, so
          // counting them would make the resume wait for rows nothing is going
          // to mount until their module is opened again.
          moduleCount: this.run.filter((r) => r.step.parentStepId === undefined).length,
        }
      : null;

    this.stop();
    return token;
  }

  /**
   * Put a paused run back, at the step it was on.
   *
   * Re-resolves everything from scratch, which is the point: the flow will have
   * navigated somewhere else and re-rendered the shell, so the elements captured
   * by the previous run are all stale. Sub-flows are re-opened in their original
   * order before the jump, because the destination is usually inside one of them.
   *
   * Returns whether the step was reachable. False is a real outcome rather than an
   * error — a module can be hidden by the time we come back — and leaves the tour
   * running at its first step rather than not running at all.
   */
  async resumeFrom(token: TourResumeToken, tour: Tour): Promise<boolean> {
    if (TourEngine.isAnyTourActive()) return false;

    /**
     * 🔴 WAIT FOR THE PAGE BEFORE REBUILDING THE RUN, OR THE TOUR COMES BACK
     * TRUNCATED — reported as "Skip module jumps straight to the last module".
     *
     * `start()` resolves every top-level anchor ONCE, synchronously, and keeps
     * only what was on screen at that instant. That is fine when the user opens
     * a tour from a settled page. It is not fine here: this runs the moment a
     * guided flow ends, and a flow ends wherever it happened to be — for the
     * automation walkthrough that is the flow BUILDER route, where `AppShell`
     * renders NO sidebar and NO top bar at all (`isFullscreenBuilder`). The app
     * is still navigating back to the list when this fires, so almost nothing
     * resolves and the rebuilt run was a single stop. The tracker then honestly
     * reported "Module 1 / 1" on whatever that stop happened to be — which reads
     * as Skip module having jumped to the end.
     *
     * So the resume waits for the host to actually be there. The token carries
     * the count the run had a moment ago, which is the only trustworthy
     * yardstick: a tour has no fixed size (modules are gated by plan,
     * permissions and the hidden-modules list), so any hard-coded minimum would
     * be wrong for somebody.
     *
     * Bounded, and it never REFUSES to resume: a module genuinely removed while
     * the flow ran (a plan change, a route that hid a row) must not strand the
     * user with no tour. Running out of budget resumes with whatever is there,
     * which is the old behaviour and no worse than it.
     */
    await this.waitForHostToSettle(tour, token.moduleCount);

    // Through `open`, not `start`: the flow just navigated the app and may have
    // left the navigation in a shape the run cannot be given in.
    if (!(await this.open(tour)).started) return false;

    for (const parentStepId of token.expanded) {
      // Sequential on purpose, though `expandChildren` would now queue these
      // correctly even if fired together — awaiting one at a time keeps this
      // loop's own ordering obvious to read.
      await this.expandChildren(parentStepId);
    }

    // AWAITED, so a caller that wants to move on from the resumed step is
    // reading the position it actually landed on. See `goTo`.
    return this.goTo(token.stepId);
  }

  /**
   * Hold until the host has mounted enough of itself to resolve a run against.
   *
   * Polls the tour's own TOP-LEVEL anchors — the same set `start()` is about to
   * resolve — and returns as soon as as many resolve as the paused run had, or
   * once the count has stopped growing, or on timeout. Whichever comes first;
   * this is a best-effort wait, never a gate.
   *
   * 🔴 "STOPPED GROWING" IS THE SECOND EXIT AND IT IS THE IMPORTANT ONE. The
   * `want` target is not always reachable: a module can be legitimately gone by
   * the time the flow ends (a plan change, a row the current route hides), and
   * waiting the full budget for a row that is never coming would put a visible
   * stall on every such resume. A count that has held still for several frames
   * is the page saying it is done.
   *
   * Frame-driven rather than on a timer, because what is being waited for is
   * React commits — and `requestAnimationFrame` is the clock those run on.
   */
  private async waitForHostToSettle(tour: Tour, want: number): Promise<void> {
    const topLevel = tour.steps.filter((s) => s.parentStepId === undefined);
    if (topLevel.length === 0) return;

    const resolvable = () => topLevel.reduce((n, s) => (resolveAnchor(s.anchor) ? n + 1 : n), 0);
    const nextFrame = () =>
      new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));

    const deadline = Date.now() + HOST_SETTLE_TIMEOUT_MS;
    let previous = -1;
    let stillSince = Date.now();

    while (Date.now() < deadline) {
      const count = resolvable();
      // Everything we had is back — nothing to wait for. This is the exit a
      // settled page takes, on the first pass, at no cost.
      if (want > 0 && count >= want) return;

      if (count !== previous) {
        previous = count;
        stillSince = Date.now();
      } else if (
        // 🔴 ONLY ONCE SOMETHING IS THERE. Zero holding steady just means the
        // page has not started mounting, which is exactly the state this exists
        // to wait out — treating it as settled would reinstate the bug.
        count > 0 &&
        Date.now() - stillSince >= HOST_SETTLE_STILL_MS
      ) {
        return;
      }

      await nextFrame();
    }
  }

  /** Tear down on unmount so a route change can never orphan the overlay. */
  destroy(): void {
    this.stop();
  }

  /* ---------------------------------------------------------------- *
   * Keeping the host's markup intact.
   * ---------------------------------------------------------------- */

  /** Snapshot the attributes driver.js is about to take over, once per element. */
  private rememberHostAria(element: Element | undefined): void {
    if (!(element instanceof HTMLElement) || this.hostAria.has(element)) return;
    this.hostAria.set(
      element,
      DRIVER_ARIA_ATTRS.map((attr) => element.getAttribute(attr)),
    );
  }

  private restoreHostAria(): void {
    this.hostAria.forEach((values, element) => {
      DRIVER_ARIA_ATTRS.forEach((attr, i) => {
        const original = values[i];
        // Absent originally means absent now: driver.js has already removed its
        // own value, so there is nothing to put back.
        if (original === null) element.removeAttribute(attr);
        else element.setAttribute(attr, original);
      });
    });
    this.hostAria = new Map();
  }

  /* ---------------------------------------------------------------- *
   * Staying aligned with a layout that moves.
   * ---------------------------------------------------------------- */

  /**
   * Keep the highlight on the right element, and around it, for as long as the
   * run lasts.
   *
   * 🔴 DRIVER.JS'S OWN LISTENERS ARE NOT ENOUGH, IN TWO SEPARATE WAYS.
   *
   * 1. It listens for `scroll` ON `window`, and scroll DOES NOT BUBBLE. The
   *    sidebar's own list is an overflow container and `<main>` is another, so
   *    scrolling either — which is exactly what happens on a short viewport,
   *    where the module the tour is pointing at is often below the fold — moves
   *    the element without driver ever hearing about it. The stage stayed where
   *    the row used to be. Capture-phase on the DOCUMENT hears every scroller
   *    on the page. (`TaskEngine` already solved this for the flow chrome; this
   *    is the same fix for the tour.)
   *
   * 2. Its resize handler RE-MEASURES THE SAME ELEMENT, which is the wrong move
   *    when a width change swaps the sidebar for a different component. The
   *    element it was holding is then detached or hidden, and a detached node
   *    measures 0x0 at 0,0 — the stage collapses into the top-left corner and
   *    the popover points at nothing. So the anchor is re-RESOLVED first (see
   *    `syncActiveAnchor`) and only then refreshed.
   *
   * `passive`, because both handlers only read.
   *
   * 🔴 COALESCED TO ONE FRAME. A document-wide capture listener hears every
   * scroller on the page, and the work behind it reads `getBoundingClientRect`
   * and `getComputedStyle` — both of which force layout. Run once per event that
   * is a forced synchronous reflow per scroll tick on the app's own scroll
   * container: the tour would make the page it is describing feel worse than it
   * is. driver.js's own `refresh` is already frame-coalesced internally; this
   * puts our half on the same footing.
   *
   * 🔴 A RESIZE IS NOT A SCROLL, AND THE TWO FRAMES DO DIFFERENT WORK. Both are
   * registered on the same coalesced handler — one frame, whichever fired — but a
   * frame that saw a resize goes through `adaptToViewport`, which may re-prepare
   * the navigation and re-resolve the whole run. A scroll only ever moves the
   * element the run is already holding, so it stays on the cheap path. Doing the
   * resize work on scroll would re-resolve the run while the user drags a
   * scrollbar; doing only the scroll work on resize is the bug this describes.
   */
  private startTracking(instance: Driver): void {
    let frame: number | null = null;
    let resized = false;

    const sync = () => {
      frame = null;
      if (this.instance !== instance) return;
      if (resized) {
        resized = false;
        void this.adaptToViewport(instance);
        return;
      }
      // Nothing changed shape, so the element is either still right or has just
      // moved — re-point it if it went away, otherwise only re-measure.
      if (this.syncActiveAnchor()) this.readopt(instance);
      else instance.refresh();
    };

    const onLayoutChange = (event: Event) => {
      if (event.type === 'resize') resized = true;
      if (frame !== null) return;
      frame = window.requestAnimationFrame(sync);
    };

    document.addEventListener('scroll', onLayoutChange, { capture: true, passive: true });
    window.addEventListener('resize', onLayoutChange, { passive: true });

    this.stopTracking = () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      document.removeEventListener('scroll', onLayoutChange, { capture: true });
      window.removeEventListener('resize', onLayoutChange);
    };
  }

  /**
   * Make a live run fit the viewport it now has.
   *
   * 🔴 THE FIX FOR "THE RING IS ON ONE MODULE AND THE POPOVER IS ABOUT ANOTHER".
   * Crossing a breakpoint does not move the sidebar, it REPLACES it: rail, icon
   * rail and drawer are three different component trees (see the block comment
   * in `anchors.ts`). Every element a run resolved before the crossing is then
   * detached or `display: none`, and both measure 0×0 at 0,0 — so driver.js drew
   * the stage as a small box in the top-left corner, which is where the first
   * sidebar rows sit, while the popover went on describing the module the run was
   * actually on. Re-measuring, which is all driver.js and the scroll path do,
   * cannot fix that: the element itself is the wrong element.
   *
   * So, in order, and only when the shape actually changed:
   *   1. ASK FOR A USABLE SHAPE AGAIN. Narrowing past the mobile breakpoint
   *      leaves no sidebar in the document at all — nothing to re-resolve
   *      against — so the drawer has to be re-opened first, exactly as at start.
   *   2. RE-RESOLVE THE WHOLE RUN, not just the step on screen. Steps ahead of
   *      the cursor were resolved against the old tree too, and `ensureContext`
   *      only re-resolves the one being moved to; a stale entry two steps ahead
   *      is the same top-left box, just later.
   *   3. RE-SAMPLE WHICH MODULES HAVE A GROUP. An icon rail has no accordions.
   *
   * Then, on every resize whether the shape changed or not, the step on screen is
   * re-pointed if it went away and brought back into view if the new viewport
   * pushed it out of the sidebar's scroller — the height half of the same report.
   */
  private async adaptToViewport(instance: Driver): Promise<void> {
    const shape = sidebarShape();

    if (shape !== this.shape) {
      this.shape = shape;
      // 🔴 ONLY IF THE STEP ON SCREEN WANTS THE NAVIGATION. Re-preparing it
      // unconditionally re-opens the drawer — which is the correct move for a
      // module stop and exactly the wrong one for a stop the drawer would cover
      // (see `ensureNavFor`), so a resize during one of those used to undo the
      // very thing that made it visible.
      const active = this.run[instance.getActiveIndex() ?? -1];
      if (!active || anchorTargetsSidebar(active.step.anchor)) {
        this.mergeSidebarRestore(await ensureSidebarUsable());
      }
      // Awaited a shape change: the run may have been torn down meanwhile.
      if (this.instance !== instance) return;
      // Re-read: `ensureSidebarUsable` is what puts a drawer or an icon rail
      // into the shape the rest of this depends on.
      this.shape = sidebarShape();
      const moved = this.reresolveRun(instance.getActiveIndex() ?? -1);
      this.recountExpandable();
      if (moved) {
        this.readopt(instance);
        return;
      }
    }

    if (this.syncActiveAnchor()) {
      this.readopt(instance);
      return;
    }

    const entry = this.run[instance.getActiveIndex() ?? -1];
    if (!entry) {
      instance.refresh();
      return;
    }

    // `readopt` re-highlights, which is what scrolls a clipped row back into
    // view — driver.js only does that when it takes an element, not on refresh.
    // It also re-renders the popover, and therefore re-runs the fit below.
    if (keepAnchorInView(entry.el)) {
      this.readopt(instance);
      return;
    }

    // 🔴 A MODULE'S GROUP IS RE-JUDGED AGAINST THE NEW SCREEN FIRST. Whether the
    // whole open group can be framed, or only the module's own row, is a
    // question about how tall the window is — so the answer given on a desktop
    // is the wrong one the moment the window is a phone, and the other way
    // round. See `rescopeActive`.
    if (this.rescopeActive(instance)) {
      this.readopt(instance);
      return;
    }

    // 🔴 THE FIT HAS TO RUN AGAIN HERE, and this is the only path that can do
    // it. A resize does not re-render the popover — driver.js's own resize
    // handler only re-measures the stage and re-places the card — so the hook
    // that normally places the guide and makes room never fires, while both
    // answers have just changed: the window has a different width (so a card
    // that fitted beside the module may no longer, or may again) and a different
    // height (so the band the module has to sit in has moved). Resizing is
    // exactly the case in the report, and without this the run keeps the
    // placement the window had when the step opened.
    this.fitStep(instance.getActiveIndex() ?? -1, entry, liveGuide());
    instance.refresh();
    // A resize reflows the whole list, and a reflow is not instant either.
    this.settleLayout(instance);
  }

  /**
   * Place the guide for the screen it is on, and give the module it describes the
   * room that placement needs. See `room.ts` for both decisions.
   *
   * Called at the two moments a step's geometry can change: when its card is
   * rendered, and when the window is resized. Nothing else needs it — a scroll
   * moves the module but changes neither the room beside it nor the size of the
   * screen, and re-fitting on every scroll frame would take the list out from
   * under the user's own scrolling.
   */
  private fitStep(index: number, entry: RunEntry, card: HTMLElement | null): void {
    // 🔴 UNCLIP BEFORE FITTING. driver.js scrolls a target into view only when
    // it fails ITS OWN test, which is viewport containment alone — a row
    // scrolled out of the sidebar's own list passes that test while sitting
    // behind the edge of the list, and the fit below would then aim a perfectly
    // good band at a row nobody can see. `keepAnchorInView` is the test that
    // knows about scroll containers (see `isAnchorClipped`).
    keepAnchorInView(entry.el);

    const placement = guideMode(entry.el.getBoundingClientRect());
    dressGuide(card, placement);

    // 🔴 THE STEP'S OWN `side` IS OVERWRITTEN, and it has to be. driver.js reads
    // `popover.side` off the step object at placement time, and the authored
    // value is always "right" — which is a description of where the guide looks
    // best, not a claim that there is room there. Written before the placement
    // that reads it (see `onPopoverRender`), and re-written on every resize.
    const driveStep = this.driveSteps[index];
    if (driveStep?.popover) {
      // A sheet is positioned by the stylesheet, so the side only decides which
      // way driver.js points its arrow — which the sheet hides. "bottom" is the
      // honest one to leave behind for anything that reads it.
      driveStep.popover.side = placement.side ?? 'bottom';
    }

    // Measured AFTER the placement is stamped: a sheet is a different shape from
    // a card beside a row — full width, its own height cap — so its height is
    // not the height the same copy had a moment ago.
    const measured = card?.getBoundingClientRect().height ?? 0;
    makeRoomFor(entry.el, {
      height: measured > 0 ? measured : guideHeight(),
      sheet: placement.mode === 'sheet',
    });
  }

  /**
   * Re-decide whether the step on screen can frame its module's whole group, now
   * that the screen is a different size, and re-point it when the answer changed.
   *
   * Returns whether it moved, because a move has to be handed to driver.js — see
   * `readopt`.
   *
   * 🔴 BOTH DIRECTIONS. Widening or growing a window has to be able to give the
   * group back, not only take it away: a run that started on a phone and was
   * moved to a laptop would otherwise keep ringing one row for a module whose
   * submodules are sitting right there with room to spare.
   *
   * Only ever asked of a module whose group this run actually opened: a closed
   * group has no rows in the DOM to frame, and a module with no group has nothing
   * to decide.
   */
  private rescopeActive(instance: Driver): boolean {
    const index = instance.getActiveIndex() ?? -1;
    const entry = this.run[index];
    if (!entry || !this.expanded.has(entry.step.id)) return false;

    const labels =
      entry.scopeLabels ??
      this.moduleLabelsFor(this.childrenByParent.get(entry.step.id) ?? []);
    if (labels.length === 0) return false;

    const scope = accordionScopeFor(labels);
    if (!scope) return false;

    // Judged against the space the group can be SEEN in — the sidebar's own
    // scrolling list, which ends above the Nexa AI entry — and not against the
    // window, which is ~150px more room than the list has. See `visibleBand`.
    const fits = groupFits(scope.getBoundingClientRect().height, {
      height: guideHeight(),
      sheet: guideMode(scope.getBoundingClientRect()).mode === 'sheet',
    }, visibleBand(scope));

    if (fits && !entry.scopeLabels) {
      entry.scopeLabels = labels;
      this.retarget(index, scope);
      return true;
    }

    if (!fits && entry.scopeLabels) {
      // Back to the module's own row. The submodules are still named in the
      // module's copy and still get their own stops — nothing is lost but the
      // combined ring, which is the thing that did not fit.
      const row = resolveAnchor(entry.step.anchor)?.element;
      if (!row) return false;
      entry.scopeLabels = undefined;
      this.retarget(index, row);
      return true;
    }

    return false;
  }

  /**
   * Make driver.js actually ADOPT the element a retarget just recorded.
   *
   * 🔴 `refresh()` DOES NOT, AND THAT IS WHY RE-RESOLVING LOOKED LIKE IT DID
   * NOTHING. driver.js's refresh re-measures `state.__activeElement` — the node
   * it took when it last highlighted — and never re-reads the steps array. So
   * every retarget on the resize path was written into `run` and `driveSteps`,
   * both correct, and then thrown away by the very call meant to apply it: the
   * stage stayed on the detached node until the user pressed Next. `moveTo` is
   * the operation that re-reads the array (see the note on `driveSteps`), and it
   * also brings the new element back into view, which refresh does not.
   *
   * Falls back to a plain refresh when there is no active entry to move to,
   * rather than calling `moveTo(-1)`, which driver.js reads as "finish".
   */
  private readopt(instance: Driver): void {
    const index = instance.getActiveIndex() ?? -1;
    if (index < 0 || !this.run[index]) {
      instance.refresh();
      return;
    }
    instance.moveTo(index);
    this.settleLayout(instance);
  }

  /**
   * Re-point every step in the run at whatever now represents its target, and
   * say whether the step on screen was one of them.
   *
   * Run once per SHAPE change, never per frame: it is a full resolve pass over
   * every step, which is affordable at a breakpoint crossing and not on a drag.
   *
   * A step whose group is closed cannot resolve — a closed accordion has no rows
   * in the DOM — and is left alone rather than cleared; `ensureContext` reopens
   * its group and re-resolves it at the moment the run moves there.
   */
  private reresolveRun(activeIndex: number): boolean {
    let activeMoved = false;
    for (let index = 0; index < this.run.length; index++) {
      const entry = this.run[index];
      if (entry.el.isConnected && isVisible(entry.el)) continue;
      const fresh = this.freshElementFor(entry);
      if (!fresh || fresh === entry.el) continue;
      this.retarget(index, fresh);
      if (index === activeIndex) activeMoved = true;
    }
    return activeMoved;
  }

  /**
   * Which modules have a group on screen right now, with their child count.
   * See `expandableCounts` for why this is re-sampled rather than resolved once.
   */
  private recountExpandable(): void {
    const counts = new Map<string, number>();
    for (const { step } of this.run) {
      const children = this.childrenByParent.get(step.id);
      if (!children || children.length === 0) continue;
      const labels = this.moduleLabelsFor(children);
      if (labels.length === 0 || !accordionTriggerFor(labels)) continue;
      counts.set(step.id, children.length);
    }
    this.expandableCounts = counts;
  }

  /**
   * Fold a further navigation change into what teardown has to undo.
   *
   * 🔴 OR-ED RATHER THAN OVERWRITTEN. `open` records the FIRST change so a
   * re-prep cannot lose it, but a run that expanded the rail at start and then
   * opened the drawer after a resize has made TWO changes, and remembering only
   * one leaves the other on screen after the tour ends — a navigation drawer
   * covering the page the tour just finished describing.
   */
  private mergeSidebarRestore(next: SidebarRestore | null): void {
    if (!next) return;
    const held = this.sidebarRestore;
    this.sidebarRestore = held
      ? {
          openedDrawer: held.openedDrawer || next.openedDrawer,
          expandedRail: held.expandedRail || next.expandedRail,
        }
      : next;
  }

  /**
   * Whatever now represents a step's target, or null when nothing does.
   *
   * A step whose sub-flow is open resolves to its ACCORDION ITEM, not to its
   * trigger row — see `RunEntry.scopeLabels`.
   */
  private freshElementFor(entry: RunEntry): HTMLElement | null {
    return entry.scopeLabels
      ? accordionScopeFor(entry.scopeLabels)
      : resolveAnchor(entry.step.anchor)?.element ?? null;
  }

  /**
   * Re-point the active step at whatever now represents its target, when the
   * element it was holding has stopped being the right one. Returns whether it
   * moved, because a move has to be handed to driver.js — see `readopt`.
   *
   * The test is deliberately "is this element still usable" rather than "has the
   * layout changed": a row that is still connected and still visible IS the
   * right element, and re-resolving on every scroll frame would be pure work.
   * `isVisible` is what catches the breakpoint swap — below the shell's mobile
   * width the desktop rail is still in the document, merely `display: none`, so
   * `isConnected` alone would happily keep highlighting it.
   *
   * A step whose sub-flow is open is re-resolved to its ACCORDION ITEM, not to
   * its trigger row — see `RunEntry.scopeLabels`. When nothing can be resolved
   * the old element is kept: a stale highlight is poor, but dropping to no
   * element at all makes driver.js draw the stage over the whole page.
   */
  private syncActiveAnchor(): boolean {
    const instance = this.instance;
    if (!instance) return false;

    const index = instance.getActiveIndex() ?? -1;
    const entry = this.run[index];
    if (!entry) return false;
    if (entry.el.isConnected && isVisible(entry.el)) return false;

    const fresh = this.freshElementFor(entry);
    if (!fresh || fresh === entry.el) return false;

    this.retarget(index, fresh);
    return true;
  }

  /**
   * Point BOTH views of a step at an element: the engine's own entry and the
   * array driver.js reads from. Updating one leaves the other stale, and which
   * one wins depends on whether the next move is a re-measure or a transition.
   */
  private retarget(index: number, element: HTMLElement): void {
    const entry = this.run[index];
    if (entry) entry.el = element;
    const driveStep = this.driveSteps[index];
    if (driveStep) driveStep.element = element;
  }

  /* ---------------------------------------------------------------- *
   * Navigation.
   * ---------------------------------------------------------------- */

  /**
   * Run `fn` once any expansion already queued has settled — see
   * `expandChain` — and only if this engine still owns the run.
   *
   * 🔴 WITHOUT THIS, "NEXT" RACES THE AUTO-EXPAND SPLICE. A module with
   * submodules queues its expansion the instant it is highlighted (see
   * `onHighlightStarted`), but that resolves at least a frame later — it has to
   * wait for the sidebar to actually open and the rows to mount. Next is
   * computed as `activeIndex + 1`; fired inside that window, it reads the OLD
   * run and lands on whatever used to sit there (the following module),
   * skipping the freshly revealed submodules entirely. Every caller that
   * advances the run funnels through here first so that can't happen.
   */
  private async afterPendingExpand(instance: Driver, fn: () => void): Promise<void> {
    await this.expandChain;
    if (this.instance !== instance) return;
    fn();
  }

  /**
   * Move to a step by index, making sure it is actually on screen first.
   *
   * An index past the end finishes the run, which is what driver.js's own Done
   * button does and what `next()` on the last step should mean.
   */
  private async navigateTo(index: number): Promise<void> {
    const instance = this.instance;
    if (!instance) return;

    const entry = this.run[index];
    if (!entry) {
      instance.destroy();
      return;
    }

    await this.ensureContext(entry, index);
    // Re-read: awaiting the group means the run could have been torn down (the
    // user pressed Escape, or the provider unmounted) while we waited.
    if (this.instance !== instance) return;
    instance.moveTo(index);
    // The move is what makes a group open or close, and neither finishes in this
    // frame. See `settleLayout`.
    this.settleLayout(instance);
  }

  /**
   * Leave exactly ONE element carrying driver.js's active-element class.
   *
   * 🔴 IT LEAKS, AND THE LEAK IS LOAD-BEARING BECAUSE OF WHAT KEYS OFF IT.
   * driver.js clears the class from one element — the `__activeElement` it
   * recorded — and that record is only written on the first frame AFTER a
   * highlight. This module re-highlights the same step within that window on
   * purpose: a module's group opens, the step is re-pointed at the whole
   * accordion item, and `reanchor` moves to it again (see `expandChildren`). The
   * second highlight therefore clears the class from the step BEFORE it, not from
   * the trigger row it is replacing — so the row keeps the mark for the rest of
   * the run. Measured: 2 elements marked on the Inbox stop, 3 by the Automations
   * stop.
   *
   * That matters because driver's own stylesheet selects on it:
   * `:has(> .driver-active-element)` is what applies `overflow: hidden` to a
   * container, and a sidebar `<li>` is a flex item — so a stale mark kept a module
   * (and every submodule inside it) collapsed to nothing for the remainder of the
   * tour, long after the tour had moved on. The stylesheet fix above stops the
   * clipping; this stops the mark surviving at all, which is the actual defect.
   *
   * Runs in `onHighlightStarted`, which driver.js calls BEFORE it writes the class
   * onto the incoming element — so the element being highlighted is never the one
   * cleared, including when the same element is re-highlighted.
   */
  private dropStaleActiveMarks(current: Element | undefined): void {
    document.querySelectorAll('.driver-active-element').forEach((el) => {
      if (el === current) return;
      el.classList.remove('driver-active-element', 'driver-no-interaction');
    });
  }

  /**
   * Put the navigation into the shape THIS step needs, on a screen where the two
   * cannot both be satisfied at once.
   *
   * 🔴 ONLY THE DRAWER FORCES A CHOICE. A rail sits beside the page, so a step on
   * a sidebar row and a step on the header are equally visible next to it and
   * nothing has to move. The drawer is a modal sheet over the whole page, and the
   * run opens it at start because most stops are navigation rows — so the stops
   * that are NOT (the header's Ask AI control) were being highlighted behind it:
   * a hole cut in the stage over something the user could not see, with the guide
   * describing it.
   *
   * So the drawer follows the step: opened for anything in the navigation, closed
   * for anything else, and reopened by the next stop that needs it. Teardown
   * still puts the user's own shape back — see `SidebarRestore`.
   */
  private async ensureNavFor(entry: RunEntry): Promise<void> {
    const wantsNav = anchorTargetsSidebar(entry.step.anchor);
    const shape = sidebarShape();

    if (wantsNav) {
      // 'absent' is the drawer while it is shut: there is no sidebar in the
      // document at all, so a navigation step has nothing to resolve against.
      if (shape === 'absent') this.mergeSidebarRestore(await ensureSidebarUsable());
      return;
    }

    if (shape === 'drawer') await closeSidebarDrawer();
  }

  /**
   * Leave the current stage's module open, and every other module closed.
   *
   * 🔴 EVERY MOVE, NOT ONLY THE MOVES THAT OPEN SOMETHING. The host closes a group
   * when another one opens, which covers a walk from one module with submodules to
   * the next — and covers nothing else. Walking to a module with NO submodules
   * clicks no trigger, so the previous module's rows stay open behind the one being
   * described; on a short viewport those rows are most of the screen, and the
   * module the guide is talking about is pushed out of the list under the ring.
   * See `collapseAccordionsExcept`.
   *
   * 🔴 AND IT OPENS THE ONE IT KEEPS, which is the other half of the same rule.
   * Closing groups on the way forward means a module REVISITED — Back from the
   * next module, or a jump from the tracker — arrives with the group it is
   * supposed to be showing shut, so its stop would describe submodules ("Includes:
   * …") that are not on screen. `openAccordion` is idempotent, so this costs
   * nothing on the common path where the group is open already.
   */
  private async prepareGroups(entry: RunEntry): Promise<void> {
    const keep = this.keepOpenLabelsFor(entry);
    await collapseAccordionsExcept(keep);
    if (keep.length > 0) openAccordion(keep);
  }

  /**
   * Which module's group this step needs open — and by omission, which ones it
   * does not.
   *
   * A submodule stop needs its own parent (it IS one of that group's rows). A
   * module stop with submodules of its own needs its own group, which is what its
   * combined highlight frames. Anything else — a module with nothing under it, the
   * header's Ask AI stop — needs nothing open at all.
   */
  private keepOpenLabelsFor(entry: RunEntry): string[] {
    if (entry.step.requiresModule) return [entry.step.requiresModule];
    return this.moduleLabelsFor(this.childrenByParent.get(entry.step.id) ?? []);
  }

  /**
   * Follow the layout until it stops moving, then stop.
   *
   * 🔴 THE MISSING HALF OF "IT IS NOT HIGHLIGHTING THE MODULE CORRECTLY IN THE
   * FLOW". Every other path in this engine reacts to an EVENT — a scroll, a
   * resize — and a group opening or closing is neither. It is an animated height
   * change with no event at all: Radix animates the panel, the rows below it slide
   * for ~200ms, driver.js animates its own stage for 400ms, and the one
   * measurement taken when the step changed described none of those frames. The
   * ring settles around where the rows were passing through, and the module the
   * guide was placed for is somewhere else by the time anyone looks — which on a
   * short screen means off it.
   *
   * So after every move the run watches the box it is highlighting and re-fits
   * whenever it has moved, until it holds still for two frames or the budget runs
   * out. That is also the auto-scroll the flow needs: the module is carried into
   * place as the list rearranges around it rather than once, early, against a
   * layout that was still in motion.
   *
   * One loop at a time — a new step cancels the previous step's — and it costs
   * nothing once the layout is still, which is the case within a few frames.
   */
  private settleLayout(instance: Driver): void {
    if (this.settleFrame !== null) window.cancelAnimationFrame(this.settleFrame);

    const startedAt = Date.now();
    let last = '';
    let still = 0;

    const tick = () => {
      this.settleFrame = null;
      // Torn down, or moved on to another step which started its own loop.
      if (this.instance !== instance) return;

      const index = instance.getActiveIndex() ?? -1;
      const entry = this.run[index];
      if (!entry) return;

      // 🔴 EVERY TICK, INCLUDING THE ONES THAT CHANGE NOTHING. This is the first
      // moment in a step's life when the guide has actually been POSITIONED —
      // driver.js places it after the render hook — so it is the only place that
      // can tell whether the tracker is sitting on top of it. See
      // `keepTrackerClear`.
      keepTrackerClear();

      const rect = entry.el.getBoundingClientRect();
      // Rounded: sub-pixel jitter during an animation would never compare equal
      // and the loop would run for its whole budget on a layout that has stopped.
      const box = `${Math.round(rect.top)}:${Math.round(rect.height)}`;

      if (box === last) {
        still += 1;
      } else {
        still = 0;
        last = box;
        // 🔴 THE FIT IS RE-RUN, NOT JUST THE MEASUREMENT. Where the module has to
        // sit depends on its own height, and its height is what the animation is
        // changing — a group halfway open needs a different band from the same
        // group open.
        this.fitStep(index, entry, liveGuide());
        instance.refresh();
      }

      if (still >= SETTLE_STILL_FRAMES || Date.now() - startedAt >= SETTLE_MS) return;
      this.settleFrame = window.requestAnimationFrame(tick);
    };

    this.settleFrame = window.requestAnimationFrame(tick);
  }

  /**
   * Put the sidebar back into the state a step needs before highlighting it.
   *
   * 🔴 ONLY ONE GROUP IS OPEN AT A TIME — the host closes the previous one when
   * a new one opens — so a submodule row that was expanded earlier is UNMOUNTED
   * once the tour moves into another module's sub-flow. Its element reference is
   * then detached, and driver.js would happily "highlight" a node that is no
   * longer in the document: the stage collapses to the top-left corner of the
   * page and the popover points at nothing. Reopening the group and re-resolving
   * the row is what makes Back across a group boundary, and a tracker jump into a
   * collapsed group, land on the real thing.
   *
   * 🔴 EVERY STEP IS RE-VALIDATED HERE, NOT ONLY A SUBMODULE ROW. This used to
   * return immediately for any step with no `requiresModule` — i.e. for every
   * module stop — on the reasoning that only a closed group unmounts its rows.
   * A BREAKPOINT CROSSING unmounts all of them: rail, icon rail and drawer are
   * three different component trees, so after a resize every element the run
   * captured belongs to a tree that is gone, and `moveTo` handed driver.js a
   * detached node — 0×0 at 0,0, a highlight in the top-left corner of the page
   * with a popover describing a module nowhere near it. Re-resolving costs one
   * query per move and is what makes Next survive a resize.
   *
   * `isVisible` as well as `isConnected`, for the same reason: below the mobile
   * breakpoint the desktop rail is still in the document, merely `display: none`,
   * so a connectedness test alone happily keeps a row nobody can see.
   */
  private async ensureContext(entry: RunEntry, index: number): Promise<void> {
    // 🔴 BEFORE THE VISIBILITY CHECK BELOW, NOT AFTER IT. A control behind the
    // navigation drawer is still `isVisible` — the drawer covers it, it does not
    // hide it — so the early return would keep the drawer over a step that needs
    // it out of the way, which is what a phone-width run did for every stop
    // outside the navigation. See `ensureNavFor`.
    await this.ensureNavFor(entry);
    // Also before the check: a module whose group is closed here is a module
    // whose rows stop taking up the screen, which moves this step's own target.
    await this.prepareGroups(entry);

    if (entry.el.isConnected && isVisible(entry.el)) return;

    const moduleLabel = entry.step.requiresModule;
    if (moduleLabel) {
      openAccordion([moduleLabel]);
      const fresh = await waitForDom(
        () => resolveAnchor(entry.step.anchor),
        SUBMENU_MOUNT_TIMEOUT_MS,
      );
      if (fresh) this.retarget(index, fresh.element);
      return;
    }

    // A module stop has no group to open: whatever now stands for it is either
    // in the document already or is not there at all.
    const fresh = this.freshElementFor(entry);
    if (fresh) this.retarget(index, fresh);
  }

  /* ---------------------------------------------------------------- *
   * Submodule sub-flows.
   * ---------------------------------------------------------------- */

  private driveStep({ step, el }: RunEntry): DriveStep {
    return {
      element: el,
      popover: {
        title: step.title,
        description: this.describeStep(step),
        side: step.side ?? 'right',
        align: 'start',
      },
    };
  }

  /**
   * A step's popover copy — a module's own description, with a short summary
   * of its submodules appended.
   *
   * 🔴 Told UP FRONT, not left implicit. Next now flows straight into a
   * module's submodules with no button in between (see `expandChildren`), so
   * the module's own stop is the only moment to say what is about to open —
   * without this line, submodule stops would arrive with no introduction at
   * all, reading as unannounced extra steps rather than the module's own
   * contents.
   *
   * Submodule labels come from `TourStep.navLabel` — the same short form the
   * tracker uses — because the full titles are breadcrumbed
   * ("Inbox › WhatsApp") and would repeat "Inbox" four times in one sentence.
   */
  private describeStep(step: TourStep): string {
    const children = this.childrenByParent.get(step.id);
    if (!children || children.length === 0) return step.description;
    const labels = children.map((child) => child.navLabel ?? child.title);
    return `${step.description} Includes: ${labels.join(', ')}.`;
  }

  /**
   * The "build one yourself" control, or null when this step has no flow.
   *
   * This is NOT gated on anything in the DOM: a flow navigates to wherever it
   * needs to be, so there is nothing on screen yet to check. The gate is the
   * caller's `taskOffer` — which is also where the sandbox check lives, since
   * these flows write demo records and must never be offered against a real
   * workspace.
   */
  private buildTaskButton(step: TourStep): HTMLButtonElement | null {
    const offer = this.options.taskOffer?.(step.id);
    if (!offer) return null;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = TASK_CLASS;
    button.setAttribute('data-testid', 'tour-start-task');

    const label = document.createElement('span');
    label.textContent = offer.label;
    const arrow = document.createElement('span');
    arrow.className = `${TASK_CLASS}__arrow`;
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '↗';
    button.append(label, arrow);

    button.addEventListener('click', () => this.options.onTaskStart?.(step.id));

    return button;
  }

  /**
   * "Skip the rest of this module", or null when Next already does that.
   *
   * 🔴 THE TOUR NEEDED THIS AS MUCH AS A GUIDED FLOW DID. Reaching a module
   * splices its submodule stops straight into the run, so a visitor who has seen
   * enough of Inbox has to press Next through every one of its rows to reach
   * Campaigns — with the only alternative being to end the whole tour. Skip is
   * how you leave one section of something, not how you leave the something.
   *
   * Absent whenever the next stop IS the next module: a control that does exactly
   * what the button beside it does reads as a bug, and on the majority of stops
   * (a module with no sub-flow, or the last stop inside one) that is the case.
   */
  private buildSkipModuleButton(at: number): HTMLButtonElement | null {
    const nextModule = this.nextModuleIndex(this.moduleIndexFor(at));
    if (nextModule >= this.run.length || nextModule <= at + 1) return null;

    const label = this.run[nextModule]?.step.navLabel ?? null;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = SKIP_CLASS;
    button.setAttribute('data-testid', 'tour-skip-module');
    // Naming the destination is what makes this obviously not "end the tour" —
    // the fear that makes people press Next instead of the thing they want.
    button.textContent = label ? `Skip to ${label}` : 'Skip this module';
    button.addEventListener('click', () => this.nextModule());

    return button;
  }

  /**
   * Stop a click inside the popover from reading as "dismiss me" to a modal the
   * host has open.
   *
   * 🔴 NEEDED THE MOMENT THE RUN CAN OPEN THE NAVIGATION DRAWER. That drawer is
   * a modal sheet, and Radix's `DismissableLayer` under it listens for
   * `pointerdown` ON THE DOCUMENT and treats anything outside its own node as a
   * request to close. driver.js portals its popover to `<body>`, so it is outside
   * by construction — pressing Next would close the very sidebar the tour is
   * pointing at, and the run would then be highlighting rows that no longer
   * exist. Exactly the problem `modalSafeChromeProps` solves for the flow's React
   * chrome; this is the same fix for markup driver.js builds itself.
   *
   * POINTER EVENTS ONLY, never `click`: driver.js's own footer buttons need their
   * click to reach its handlers, and `click` is dispatched separately from
   * `pointerdown`, so suppressing the latter leaves activation untouched.
   * Bubble phase, because that is the phase Radix listens in.
   */
  private keepModalsOpen(wrapper: HTMLElement): void {
    const swallow = (event: Event) => event.stopPropagation();
    wrapper.addEventListener('pointerdown', swallow);
    // Some dismiss implementations in this shell watch `mousedown` instead.
    wrapper.addEventListener('mousedown', swallow);
  }

  /** Every distinct module label the given children ask to have opened. */
  private moduleLabelsFor(children: TourStep[]): string[] {
    const labels: string[] = [];
    for (const child of children) {
      const label = child.requiresModule;
      if (label && !labels.includes(label)) labels.push(label);
    }
    return labels;
  }

  /**
   * Open a module's group and splice its rows into the live run, immediately
   * after the module's own stop — WITHOUT moving there. The run simply grows to
   * include them, so the next Next naturally flows into the first one.
   *
   * Resolution happens HERE rather than at start, and after the group is open:
   * the rows do not exist in the DOM until then, so nothing about them could be
   * resolved — or measured — any earlier.
   *
   * Queued on `expandChain` rather than run immediately — see that field's
   * comment for why a synchronous guard is not enough here.
   */
  private expandChildren(parentStepId: string): Promise<void> {
    const run = async (): Promise<void> => {
      const instance = this.instance;
      if (!instance || this.expanded.has(parentStepId)) return;

      const children = this.childrenByParent.get(parentStepId);
      const at = this.runOrder.indexOf(parentStepId);
      if (!children || children.length === 0 || at < 0) return;

      // 🔴 Captured BEFORE the splice. Inserting rows shifts the index of every
      // step after them, so the step the user is standing on can end up at a
      // different position than driver.js believes it is at. The id survives
      // the shift; the index does not.
      const standingOn = this.run[instance.getActiveIndex() ?? -1]?.step.id ?? null;

      const moduleLabels = this.moduleLabelsFor(children);
      openAccordion(moduleLabels);
      const resolvedEntries = await this.resolveChildren(children);

      // Marked expanded even when nothing resolved, so a group that opened but
      // rendered no rows is not retried on every future highlight.
      this.expanded.add(parentStepId);

      // 🔴 RE-TARGET THE MODULE'S OWN HIGHLIGHT AT THE WHOLE ACCORDION ITEM —
      // trigger row AND the panel it just opened — not only the trigger. Its
      // bounding box grows the instant the panel opens, so re-highlighting
      // this same element (via `reanchor` below) draws the stage cutout around
      // the module AND its now-visible submodules together, exactly the
      // "show them highlighted together" behaviour a plain trigger element
      // could never give — a collapsed trigger and an opened one are the same
      // element with a bigger box, nothing needs to be measured or unioned by
      // hand.
      //
      // Done for every module with a group on screen, regardless of what gets
      // filtered below — the combined highlight and the module's own
      // "Includes: …" summary (see `describeStep`) are how EVERY submodule
      // gets introduced, whether or not it becomes its own Next-stop.
      const scope = accordionScopeFor(moduleLabels);
      // 🔴 ONLY WHEN THERE IS ROOM TO FRAME IT. An open module here is its
      // trigger row plus up to six submodule rows — fine on a laptop, taller
      // than the whole window on a phone in landscape, where ringing it draws a
      // stage that runs off both edges of the screen with no visible boundary
      // anywhere and leaves the guide nowhere to sit. `groupFits` is that
      // judgement; failing it, the module keeps its own row as the highlight and
      // its submodules stay introduced by the copy and by their own stops.
      // 🔴 AGAINST THE LIST'S VISIBLE BOX, NOT THE WINDOW. The two differ by the
      // height of the Nexa AI entry and the account control below the scroller,
      // which is exactly the room a group at the END of the list does not have —
      // framed against the window, Petpooja's and Support's groups were ringed as
      // one box whose last rows were behind the footer. See `visibleBand`.
      const framed =
        scope &&
        groupFits(scope.getBoundingClientRect().height, {
          height: guideHeight(),
          sheet: guideMode(scope.getBoundingClientRect()).mode === 'sheet',
        }, visibleBand(scope));
      if (scope && framed) {
        // Recorded as well as applied, so a later re-resolve re-finds the
        // accordion item rather than shrinking back to the trigger row. See
        // `RunEntry.scopeLabels`.
        const parentEntry = this.run[at];
        if (parentEntry) parentEntry.scopeLabels = moduleLabels;
        this.retarget(at, scope);
      }

      // 🔴 A SUBMODULE BECOMES ITS OWN STOP ONLY WHERE IT OFFERS A "TRY IT"
      // FLOW. This was briefly reversed to splice every resolved child, which
      // made the walk 47 stops — every one of the 33 authored submodules ringed
      // in turn. That is too long to sit through for rows whose only difference
      // from the module above them is a sentence of copy, so the rule is back:
      // a submodule earns its own stop by having something to DO there.
      //
      // Nothing is lost from the submodules themselves — the module's stop still
      // OPENS the group (see `prepareGroups`), still rings the whole accordion
      // item when it fits (see `groupFits`), and still names every child in its
      // own copy (see `describeStep`). They are shown; they are just not each
      // given a stop of their own.
      const entries = resolvedEntries.filter((entry) => this.options.taskOffer?.(entry.step.id));

      if (entries.length === 0) {
        // Re-highlight where we already are: that republishes run state, which
        // is how the tracker learns there is nothing more to reveal here, and
        // re-measures against the sidebar the open group just made taller.
        this.reanchor(instance, standingOn, at);
        return;
      }

      this.run.splice(at + 1, 0, ...entries);
      this.driveSteps.splice(at + 1, 0, ...entries.map((entry) => this.driveStep(entry)));
      this.runOrder = this.run.map((r) => r.step.id);
      this.reanchor(instance, standingOn, at);
    };

    const chained = this.expandChain.then(run, run);
    // Never let one failed expansion break the chain for whatever queues after it.
    this.expandChain = chained.catch(() => {});
    return chained;
  }

  /**
   * Re-highlight the step the user is on, at whatever index it now occupies.
   *
   * Not a no-op even when the index is unchanged: it re-renders the popover (so
   * "Step 3 of 21" and the explore button reflect the longer run) and re-emits
   * run state, which is how the tracker learns the sub-flow is now open.
   */
  private reanchor(instance: Driver, stepId: string | null, fallbackIndex: number): void {
    const index = stepId !== null ? this.runOrder.indexOf(stepId) : -1;
    instance.moveTo(index >= 0 ? index : fallbackIndex);
    // 🔴 THE MOST IMPORTANT OF THE THREE. This runs the instant a group has been
    // asked to open, so the rows it is about to reveal are mid-animation right
    // now and the module's own box is still growing. See `settleLayout`.
    this.settleLayout(instance);
  }

  /**
   * Resolve a module's submodule steps once its group has been opened.
   *
   * Waits for the FULL set — siblings mount together, so anything less usually
   * means the panel is still rendering — but accepts whatever resolved when the
   * budget runs out. A partial sub-flow is a shorter sub-flow; refusing it
   * outright would turn one plan-hidden row into a dead button.
   */
  private async resolveChildren(children: TourStep[]): Promise<RunEntry[]> {
    const attempt = (): RunEntry[] => {
      const entries: RunEntry[] = [];
      for (const step of children) {
        const hit = resolveAnchor(step.anchor);
        if (hit) entries.push({ step, el: hit.element, strategy: hit.strategy });
      }
      return entries;
    };

    const complete = await waitForDom(() => {
      const entries = attempt();
      return entries.length === children.length ? entries : null;
    }, SUBMENU_MOUNT_TIMEOUT_MS);

    return complete ?? attempt();
  }
}
